import { useCallback, useEffect, useRef } from 'react';

/**
 * Vertical drag-handle that lets the user resize a sibling column's
 * width by dragging horizontally. Used between the BuilderScreen's
 * left / center / right panels.
 *
 * The splitter is purely UI — width state lives in the parent
 * component (typically persisted to localStorage). This component
 * just translates `mousemove` while the user is dragging into a
 * stream of width values via `onResize(nextWidth)`.
 *
 *   - `side: 'left'`  → dragging right *increases* the controlled
 *                       width (the resized panel is on the LEFT of the
 *                       splitter, e.g. left sidebar).
 *   - `side: 'right'` → dragging right *decreases* the controlled
 *                       width (the resized panel is on the RIGHT of
 *                       the splitter, e.g. right sidebar).
 *
 * Min / max clamping keeps the UI usable. The body cursor is forced
 * to `col-resize` and selection is disabled while dragging so a wide
 * gesture across the iframe doesn't select text.
 */
export interface SplitterProps {
  /** Current width of the panel being resized (in px). */
  width: number;
  /** Notified on every drag tick with the candidate next width. */
  onResize: (next: number) => void;
  /** Side of the splitter relative to the resized panel. */
  side: 'left' | 'right';
  /** Lower bound for width. Defaults to 200. */
  min?: number;
  /** Upper bound for width. Defaults to 600. */
  max?: number;
}

export function Splitter({
  width,
  onResize,
  side,
  min = 200,
  max = 600,
}: SplitterProps) {
  // Captured once per drag — avoids a render cycle to update the start
  // values via state.
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);

  const handleMove = useCallback(
    (e: MouseEvent) => {
      if (!drag.current) return;
      const dx = e.clientX - drag.current.startX;
      const delta = side === 'left' ? dx : -dx;
      const next = Math.max(min, Math.min(max, drag.current.startWidth + delta));
      onResize(next);
    },
    [side, min, max, onResize],
  );

  const stopDrag = useCallback(() => {
    drag.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleMove);
    window.removeEventListener('mouseup', stopDrag);
  }, [handleMove]);

  // Defensive cleanup — if the component unmounts mid-drag (e.g. the
  // user navigates away), don't leak the global cursor / listeners.
  useEffect(() => stopDrag, [stopDrag]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    drag.current = { startX: e.clientX, startWidth: width };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', stopDrag);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      style={styles.handle}
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize"
    >
      <div style={styles.grip} />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  handle: {
    width: 6,
    flexShrink: 0,
    cursor: 'col-resize',
    backgroundColor: '#E2E8F0',
    position: 'relative',
    transition: 'background-color 100ms',
  },
  grip: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 2,
    height: 28,
    borderRadius: 1,
    backgroundColor: '#94A3B8',
    pointerEvents: 'none',
  },
};
